import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  Toolbar,
  Stack,
  Card,
  CardContent,
  InputAdornment,
  CssBaseline,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  FormLabel,
  Divider,
  Checkbox,
  Collapse,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Email as EmailIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  Launch as LaunchIcon,
  Close as CloseIcon,
  AccountBalance as OtherIcon,
  FlashOn as InstantIcon,
  Description as DocumentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import PayPalConnectButton, { PayPalVerifiedBadge } from "../../Components/PayPalConnectButton";
import StripePlatformFCButton from "../../Components/payments/StripePlatformFCButton";
import { lightTheme } from "../../Utils/baseTheme";
import client from "../../API";
import { replaceConsoleError } from '../../Utils/toastNotifications';
import StripeOnboardingRequiredModal from "../../Components/payments/StripeOnboardingRequiredModal";
import { useStripeOnboardingCheck } from "../../Hooks/useStripeOnboardingCheck";

// Email validation regex
const PAYPAL_EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const DOMAIN_CORRECTIONS = {
  'gmai.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlook.co': 'outlook.com',
};

// Fallback theme
const fallbackTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1976d2' },
    success: { main: '#2e7d32' },
    error: { main: '#d32f2f' },
    warning: { main: '#ed6c02' },
  },
});

// helpers
const money = (n = 0) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const PayoutClaimPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [payout, setPayout] = useState(null);
  const [paymentChoice, setPaymentChoice] = useState("paypal");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [speedChoice, setSpeedChoice] = useState("wait");
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [emailError, setEmailError] = useState("");
  const [emailWarning, setEmailWarning] = useState("");
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [finalAmount, setFinalAmount] = useState(null);

  // Bank ACH State
  const [bankDetails, setBankDetails] = useState({
    routing_number: '',
    account_number: '',
    account_holder_name: '',
    account_type: 'checking'
  });
  const [bankErrors, setBankErrors] = useState({});

  // Stripe Connect State
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);

  // PayPal OAuth State
  const [paypalOAuthConnected, setPaypalOAuthConnected] = useState(false);
  const [paypalOAuthEmail, setPaypalOAuthEmail] = useState('');

  // Stripe FC Bank ACH State
  const [stripeFCConnected, setStripeFCConnected] = useState(false);
  const [stripeFCBankInfo, setStripeFCBankInfo] = useState(null);
  // stripeFCBankInfo = { bank_name, last4, account_type, connect_account_id }
  const [manualBankCreating, setManualBankCreating] = useState(false);

  // Stripe onboarding guard modal state
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [onboardingBlock, setOnboardingBlock] = useState(null);

  // BlitzPay RPA State
  const [rpaStatus, setRpaStatus] = useState({
    loading: true,
    hasSigned: false,
    error: null,
  });
  const [rpaTemplate, setRpaTemplate] = useState(null);
  const [rpaCheckboxChecked, setRpaCheckboxChecked] = useState(false);
  const [showRpaDetails, setShowRpaDetails] = useState(false);
  const [rpaProcessing, setRpaProcessing] = useState(false);

  // Proactive Stripe onboarding check — runs when claim-flow creator has linked a
  // Stripe Connect account (via Stripe FC) and is about to submit a bank_ach claim.
  const {
    needsOnboarding: achNeedsOnboarding,
    missingFields: achMissingFields,
    onboardingUrl: achOnboardingUrl,
    refetch: refetchOnboardingStatus,
  } = useStripeOnboardingCheck({
    enabled: Boolean(token) && paymentChoice === 'bank_ach' && stripeFCConnected,
    mode: 'claim',
    claimToken: token,
    returnUrl: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  // Check if payout can be claimed
  // "approved" payouts without a payout_method are unclaimed (invoice was approved before external user visited)
  const canClaimPayout = !payout?.withdrawal_id && (
    payout?.status === "pending" ||
    (payout?.status === "approved" && !payout?.payout_method)
  );
  const isQueuedForManualProcessing = payout?.queued_for_manual_processing
    || payout?.withdrawal_processing_mode === 'manual_fallback';
  const isAlreadyClaimed = payout && (payout.payout_method || payout.withdrawal_id) &&
    ["approved", "confirmed", "paid", "funded"].includes(payout.status);
  const isCancelled = payout?.status === "cancelled";

  // Check if client is paying the fee (role === "Client")
  const isClientPayingFee = payout?.role === "Client";

  // Check if the payment rail is locked by the sender
  const isRailForced = payout?.payout_method_mode === 'forced' && !!payout?.forced_payment_rail;
  const hasMatchingWallet = isRailForced && payout?.has_matching_wallet === true;
  const matchingWalletDisplay = payout?.matching_wallet_display || '';

  const needsRpaAgreement = !rpaStatus.hasSigned && !rpaCheckboxChecked;

  /* ───────── Email Validation Function ───────── */
  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return { error: "Email address is required", warning: "" };
    }
    const trimmedEmail = email.trim().toLowerCase();
    
    if (trimmedEmail.length > 254) {
      return { error: "Email address is too long (max 254 characters)", warning: "" };
    }
    if (!PAYPAL_EMAIL_REGEX.test(trimmedEmail)) {
      return { error: "Please enter a valid PayPal email address", warning: "" };
    }
    
    const emailParts = trimmedEmail.split('@');
    if (emailParts.length !== 2) {
      return { error: "Invalid email format", warning: "" };
    }
    
    const [localPart, domain] = emailParts;
    if (localPart.length === 0) {
      return { error: "Email address cannot start with @", warning: "" };
    }
    if (localPart.length > 64) {
      return { error: "Email username is too long", warning: "" };
    }
    
    if (DOMAIN_CORRECTIONS[domain]) {
      const suggestion = `${localPart}@${DOMAIN_CORRECTIONS[domain]}`;
      return { error: "", warning: `Did you mean ${suggestion}?` };
    }
    
    const invalidDomains = ['example.com', 'test.com', 'domain.com', 'email.com'];
    if (invalidDomains.includes(domain)) {
      return { error: "Please enter a real email address", warning: "" };
    }
    
    if (localPart.includes('..') || localPart.startsWith('.') || localPart.endsWith('.')) {
      return { error: "Invalid email format", warning: "" };
    }
    
    return { error: "", warning: "" };
  };

  /* ───────── Bank Details Validation ───────── */
  const validateBankDetails = () => {
    const errors = {};

    // Routing number validation (9 digits)
    if (!bankDetails.routing_number || bankDetails.routing_number.length !== 9) {
      errors.routing_number = "Routing number must be 9 digits";
    } else if (!/^\d+$/.test(bankDetails.routing_number)) {
      errors.routing_number = "Routing number must contain only numbers";
    }

    // Account number validation (4-17 digits)
    if (!bankDetails.account_number || bankDetails.account_number.length < 4) {
      errors.account_number = "Account number must be at least 4 digits";
    } else if (bankDetails.account_number.length > 17) {
      errors.account_number = "Account number must be no more than 17 digits";
    } else if (!/^\d+$/.test(bankDetails.account_number)) {
      errors.account_number = "Account number must contain only numbers";
    }

    // Account holder name validation
    if (!bankDetails.account_holder_name || bankDetails.account_holder_name.trim().length < 2) {
      errors.account_holder_name = "Account holder name is required";
    }

    return errors;
  };

  /* ───────── Bank Details Change Handler ───────── */
  const handleBankDetailChange = (field) => (event) => {
    let value = event.target.value;

    // For numeric fields, only allow digits
    if (field === 'routing_number' || field === 'account_number') {
      value = value.replace(/\D/g, '');
    }

    setBankDetails(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (bankErrors[field]) {
      setBankErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  /* ───────── Check BlitzPay RPA Status by Username ───────── */
  const checkRpaStatus = async (creatorUsername) => {
    if (!creatorUsername) {
      console.error('❌ Cannot check RPA status: creator_username is required');
      setRpaStatus({
        loading: false,
        hasSigned: false,
        error: "Creator username is required",
      });
      return;
    }

    try {
      setRpaStatus(prev => ({ ...prev, loading: true }));
      
      console.log('🔍 Checking RPA status for creator:', creatorUsername);
      
      const response = await client.contracts.blitzpayRpa.checkStatus(
        creatorUsername
      );
      
      console.log('✅ RPA status response:', response);
      
      setRpaStatus({
        loading: false,
        hasSigned: response.has_signed || false,
        error: null,
      });

      if (response.has_signed) {
        setRpaCheckboxChecked(true);
      }

      if (!response.has_signed) {
        try {
          const templateResponse = await client.contracts.blitzpayRpa.getTemplate();
          setRpaTemplate(templateResponse.template);
        } catch (templateErr) {
          replaceConsoleError("Error fetching RPA template", templateErr);
        }
      }
    } catch (err) {
      replaceConsoleError("Error checking RPA status", err);
      setRpaStatus({
        loading: false,
        hasSigned: false,
        error: "Unable to verify agreement status",
      });
      
      try {
        const templateResponse = await client.contracts.blitzpayRpa.getTemplate();
        setRpaTemplate(templateResponse.template);
      } catch (templateErr) {
        // Silent fail
      }
    }
  };

  /* ───────── Handle RPA Agreement with Username ───────── */
  const handleRpaAgreement = async () => {
    if (!paypalEmail || !paypalEmail.trim()) {
      setSnack({
        open: true,
        msg: "Please enter your PayPal email first",
        sev: "warning"
      });
      return;
    }

    const { error, warning } = validateEmail(paypalEmail);
    if (error) {
      setSnack({
        open: true,
        msg: "Please enter a valid email address first",
        sev: "warning"
      });
      return;
    }

    if (!rpaCheckboxChecked) {
      setSnack({
        open: true,
        msg: "Please check the agreement checkbox to proceed",
        sev: "warning"
      });
      return;
    }

    setRpaProcessing(true);
    try {
      const signatureData = {
        creator_username: payout?.creator_id,
        signer_email: paypalEmail.trim().toLowerCase(),
        signer_name: payout?.creator_display_name || payout?.creator_id || "Creator",
        payout_token: token,
        creator_id: payout?.creator_id,
      };
      
      if (payout?.creator_phone) {
        signatureData.signer_phone = payout.creator_phone;
      }
      
      console.log('📝 Signing RPA with data:', signatureData);
      
      await client.contracts.blitzpayRpa.createAndSign(signatureData);

      setRpaStatus(prev => ({ ...prev, hasSigned: true }));
      
      setSnack({
        open: true,
        msg: "Agreement confirmed successfully!",
        sev: "success",
      });
    } catch (err) {
      replaceConsoleError("Error signing RPA", err);
      
      let errorMsg = "Failed to confirm agreement. Please try again.";
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      
      setSnack({
        open: true,
        msg: errorMsg,
        sev: "error",
      });
    } finally {
      setRpaProcessing(false);
    }
  };

  /* ───────── Check for OAuth callbacks on mount ───────── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('stripe') === 'connected') {
      setStripeConnected(true);
      setPaymentChoice('stripe');
      setSnack({ open: true, msg: "Stripe account connected successfully!", sev: "success" });
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (params.get('paypal') === 'connected') {
      const email = params.get('email') || '';
      setPaypalOAuthConnected(true);
      setPaypalOAuthEmail(email);
      if (email) setPaypalEmail(email);
      setPaymentChoice('paypal');
      setSnack({ open: true, msg: "PayPal account connected successfully!", sev: "success" });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  /* ───────── Fetch payout on mount ───────── */
  useEffect(() => {
    if (!token) {
      setSnack({
        open: true,
        msg: "Invalid payout link. Please check the URL.",
        sev: "error",
      });
      setLoading(false);
      return;
    }

    const fetchPayoutData = async () => {
      try {
        const response = await client.payouts.byToken(token);
        if (!response?.payout) {
          throw new Error("No payout data returned");
        }
        setPayout(response.payout);
        
        const creatorUsername = response.payout?.creator_id;
        const email = response.payout?.payout_email || response.payout?.creator_email;
        const phone = response.payout?.creator_phone || null;
        
        console.log('💰 Payout data loaded:', {
          creatorUsername,
          email,
          phone,
          amount: response.payout?.amount
        });
        
        if (email) {
          setPaypalEmail(email);
        }

        // If sender locked the payment rail, override the payment choice
        if (response.payout?.forced_payment_rail) {
          setPaymentChoice(response.payout.forced_payment_rail);
        }

        if (creatorUsername) {
          await checkRpaStatus(creatorUsername, email, phone);
        } else if (email) {
          console.warn('⚠️ No creator username found, falling back to email-based RPA check');
          await checkRpaStatus(null, email, phone);
        }

        if (response.payout?.role === "Client") {
          setSpeedChoice("fast");
        }
      } catch (err) {
        replaceConsoleError("Error fetching payout", err);
        setSnack({
          open: true,
          msg: "Unable to load payout – please check the link or try again.",
          sev: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPayoutData();
  }, [token]);

  /* ───────── Check RPA when email changes ───────── */
  useEffect(() => {
    if (paypalEmail && !emailError && paypalEmail.includes('@')) {
      const delayDebounce = setTimeout(() => {
        const creatorUsername = payout?.creator_id;
        const phone = payout?.creator_phone || null;
        
        if (creatorUsername) {
          checkRpaStatus(creatorUsername, paypalEmail.trim().toLowerCase(), phone);
        } else {
          checkRpaStatus(null, paypalEmail.trim().toLowerCase(), phone);
        }
      }, 500);

      return () => clearTimeout(delayDebounce);
    }
  }, [paypalEmail, emailError, payout?.creator_id, payout?.creator_phone]);

  /* ───────── PayPal email input handler ───────── */
  const handlePaypalEmailChange = (event) => {
    const value = event.target.value;
    setPaypalEmail(value);
    if (value.trim()) {
      const { error, warning } = validateEmail(value);
      setEmailError(error);
      setEmailWarning(warning);
    } else {
      setEmailError("");
      setEmailWarning("");
    }
  };

  /* ───────── Accept suggested email correction ───────── */
  const acceptSuggestion = () => {
    if (emailWarning && emailWarning.includes("Did you mean")) {
      const suggested = emailWarning.match(/Did you mean (.+?)\?/)?.[1];
      if (suggested) {
        setPaypalEmail(suggested);
        setEmailWarning("");
        setEmailError("");
      }
    }
  };

  /* ───────── Handle other payment methods redirect ───────── */
  const handleOtherPaymentMethod = () => {
    const creatorConnectUrl = payout?.creator_id ? 
      `/creatorconnect/${payout.creator_id}?action=setup_wallet&payout_token=${token}` :
      `/creatorconnect/signup?action=setup_wallet&payout_token=${token}`;
    
    window.location.href = creatorConnectUrl;
  };

  /* ───────── PayPal Submit handler ───────── */
  const handlePayPalSubmit = async () => {
    if (!canClaimPayout) {
      setSnack({
        open: true,
        msg: "This payment cannot be claimed at this time.",
        sev: "error"
      });
      return;
    }
    
    if (!paypalEmail || !paypalEmail.trim()) {
      setSnack({
        open: true,
        msg: "Please enter your PayPal email address",
        sev: "warning"
      });
      return;
    }
    
    const { error, warning } = validateEmail(paypalEmail);
    if (error) {
      setEmailError(error);
      setSnack({
        open: true,
        msg: "Please enter a valid PayPal email address",
        sev: "warning"
      });
      return;
    }
    if (warning && warning.includes("Did you mean")) {
      setEmailWarning(warning);
      setSnack({
        open: true,
        msg: "Please check your email address for typos",
        sev: "warning"
      });
      return;
    }

    if (needsRpaAgreement) {
      setSnack({
        open: true,
        msg: "Please agree to the BlitzPay RPA Agreement first",
        sev: "warning"
      });
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await client.payouts.claim(token, {
        paymentMethod: paypalEmail.trim().toLowerCase(),
        blitzpay: speedChoice === "fast",
        paymentType: "paypal",
      });
      
      if (response?.final_amount !== undefined) {
        setFinalAmount(response.final_amount);
      }
      
      setPaymentConfirmed(true);
      
      setPayout(prev => ({ 
        ...prev, 
        status: response?.payout_status || "approved",
        payout_method: `paypal:${paypalEmail.trim().toLowerCase()}`,
        accepted_date: new Date().toISOString(),
        blitzpay: speedChoice === "fast",
        withdrawal_id: response?.withdrawal_id || prev?.withdrawal_id,
        withdrawal_processing_mode: response?.withdrawal_processing_mode || prev?.withdrawal_processing_mode,
        queued_for_manual_processing: response?.queued_for_manual_processing ?? prev?.queued_for_manual_processing,
      }));
      
      setSnack({
        open: true,
        msg: response?.message || "Payout claimed successfully! Your payment method has been saved.",
        sev: "success",
      });
      
      setTimeout(() => {
        setShowNavigationDialog(true);
      }, 2000);
      
    } catch (err) {
      replaceConsoleError("Claim payout error", err);
      let errorMessage = "Something went wrong. Please try again.";
      
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
        
        if (err.response.data.suggested_email) {
          setEmailWarning(`Did you mean ${err.response.data.suggested_email}?`);
          setEmailError("Please check your email address");
          errorMessage = "Please verify your PayPal email address";
        }
      }
      
      setSnack({
        open: true,
        msg: errorMessage,
        sev: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ───────── Bank ACH Submit handler ───────── */
  const handleBankAchSubmit = async () => {
    if (!canClaimPayout) {
      setSnack({
        open: true,
        msg: "This payment cannot be claimed at this time.",
        sev: "error"
      });
      return;
    }

    // Validate bank details (skip when bank linked via Stripe FC)
    if (!stripeFCConnected) {
      const errors = validateBankDetails();
      if (Object.keys(errors).length > 0) {
        setBankErrors(errors);
        setSnack({
          open: true,
          msg: "Please fix the errors in your bank details",
          sev: "warning"
        });
        return;
      }
    }

    if (needsRpaAgreement) {
      setSnack({
        open: true,
        msg: "Please agree to the BlitzPay RPA Agreement first",
        sev: "warning"
      });
      return;
    }

    // Proactive Stripe onboarding guard for FC-linked bank accounts
    if (stripeFCConnected && achNeedsOnboarding) {
      setOnboardingBlock({
        missingFields: achMissingFields,
        onboardingUrl: achOnboardingUrl,
      });
      setOnboardingModalOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      // If bank was linked via Stripe FC, use bank_stripe_fc type
      const payload = stripeFCConnected
        ? {
            paymentMethod: `****${stripeFCBankInfo.last4}`,
            blitzpay: speedChoice === "fast",
            paymentType: "bank_stripe_fc",
            stripe_account_id: stripeFCBankInfo.connect_account_id,
            bank_last4: stripeFCBankInfo.last4,
            bank_name: stripeFCBankInfo.bank_name,
            bank_account_type: stripeFCBankInfo.account_type,
          }
        : {
            paymentMethod: `****${bankDetails.account_number.slice(-4)}`,
            blitzpay: speedChoice === "fast",
            paymentType: "bank_ach",
            bankDetails: {
              routing_number: bankDetails.routing_number,
              account_number: bankDetails.account_number,
              account_holder_name: bankDetails.account_holder_name,
              account_type: bankDetails.account_type
            }
          };

      const response = await client.payouts.claim(token, payload);

      if (response?.final_amount !== undefined) {
        setFinalAmount(response.final_amount);
      }

      setPaymentConfirmed(true);

      setPayout(prev => ({
        ...prev,
        status: response?.payout_status || "approved",
        payout_method: stripeFCConnected
          ? `bank_ach:****${stripeFCBankInfo.last4}`
          : `bank_ach:****${bankDetails.account_number.slice(-4)}`,
        accepted_date: new Date().toISOString(),
        blitzpay: speedChoice === "fast",
        withdrawal_id: response?.withdrawal_id || prev?.withdrawal_id,
        withdrawal_processing_mode: response?.withdrawal_processing_mode || prev?.withdrawal_processing_mode,
        queued_for_manual_processing: response?.queued_for_manual_processing ?? prev?.queued_for_manual_processing,
      }));

      setSnack({
        open: true,
        msg: response?.message || "Payout claimed successfully! Your bank account has been saved.",
        sev: "success",
      });

      setTimeout(() => {
        setShowNavigationDialog(true);
      }, 2000);

    } catch (err) {
      console.error("Bank ACH claim error:", err);

      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 422 && data?.error_code === 'stripe_onboarding_incomplete') {
        setOnboardingBlock({
          missingFields: data.missing_fields || [],
          onboardingUrl: data.onboarding_url,
        });
        setOnboardingModalOpen(true);
        if (refetchOnboardingStatus) refetchOnboardingStatus();
        return;
      }

      let errorMessage = "Something went wrong. Please try again.";
      if (data?.error) {
        errorMessage = data.error;
      }

      setSnack({
        open: true,
        msg: errorMessage,
        sev: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ───────── Manual Bank Create handler ───────── */
  const handleCreateManualBank = async () => {
    const errors = {};
    if (!bankDetails.routing_number || !/^\d{9}$/.test(bankDetails.routing_number)) {
      errors.routing_number = 'Must be exactly 9 digits';
    }
    if (!bankDetails.account_number || !/^\d{4,17}$/.test(bankDetails.account_number)) {
      errors.account_number = 'Must be 4–17 digits';
    }
    if (!bankDetails.account_holder_name.trim()) {
      errors.account_holder_name = 'Required';
    }
    if (Object.keys(errors).length > 0) {
      setBankErrors(errors);
      return;
    }
    setBankErrors({});
    setManualBankCreating(true);
    try {
      const result = await client.payouts.claimManualBank(token, {
        routing_number: bankDetails.routing_number,
        account_number: bankDetails.account_number,
        account_holder_name: bankDetails.account_holder_name,
      });
      setStripeFCConnected(true);
      setStripeFCBankInfo(result);
      setSnack({ open: true, msg: 'Bank account linked!', sev: 'success' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to link bank account. Please check your details.';
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setManualBankCreating(false);
    }
  };

  /* ───────── Stripe Connect handler ───────── */
  /* ───────── PayPal OAuth error handler ───────── */
  const handlePaypalOAuthError = useCallback((err) => {
    setSnack({
      open: true,
      msg: err?.message || "Failed to initiate PayPal connection. Please enter your email below.",
      sev: "warning"
    });
  }, []);

  const handleStripeConnect = async () => {
    setStripeConnecting(true);
    try {
      const response = await client.payouts.initiateStripeOAuth(token);
      if (response?.authorization_url) {
        window.location.href = response.authorization_url;
      } else {
        throw new Error("No authorization URL returned");
      }
    } catch (err) {
      console.error("Stripe connect error:", err);
      setSnack({
        open: true,
        msg: err?.response?.data?.error || "Failed to initiate Stripe connection. Please try again.",
        sev: "error"
      });
      setStripeConnecting(false);
    }
  };

  /* ───────── Stripe Submit handler ───────── */
  const handleStripeSubmit = async () => {
    if (!canClaimPayout) {
      setSnack({
        open: true,
        msg: "This payment cannot be claimed at this time.",
        sev: "error"
      });
      return;
    }

    if (!stripeConnected) {
      setSnack({
        open: true,
        msg: "Please connect your Stripe account first",
        sev: "warning"
      });
      return;
    }

    if (needsRpaAgreement) {
      setSnack({
        open: true,
        msg: "Please agree to the BlitzPay RPA Agreement first",
        sev: "warning"
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await client.payouts.claim(token, {
        paymentMethod: "stripe",
        blitzpay: speedChoice === "fast",
        paymentType: "stripe"
      });

      if (response?.final_amount !== undefined) {
        setFinalAmount(response.final_amount);
      }

      setPaymentConfirmed(true);

      setPayout(prev => ({
        ...prev,
        status: response?.payout_status || "approved",
        payout_method: "stripe:connected",
        accepted_date: new Date().toISOString(),
        blitzpay: speedChoice === "fast",
        withdrawal_id: response?.withdrawal_id || prev?.withdrawal_id,
        withdrawal_processing_mode: response?.withdrawal_processing_mode || prev?.withdrawal_processing_mode,
        queued_for_manual_processing: response?.queued_for_manual_processing ?? prev?.queued_for_manual_processing,
      }));

      setSnack({
        open: true,
        msg: response?.message || "Payout claimed successfully! Your Stripe account has been saved.",
        sev: "success",
      });

      setTimeout(() => {
        setShowNavigationDialog(true);
      }, 2000);

    } catch (err) {
      console.error("Stripe claim error:", err);
      let errorMessage = "Something went wrong. Please try again.";

      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      setSnack({
        open: true,
        msg: errorMessage,
        sev: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ───────── Existing wallet claim (seamless) ───────── */
  const handleExistingWalletClaim = async () => {
    if (!canClaimPayout) {
      setSnack({ open: true, msg: "This payment cannot be claimed at this time.", sev: "error" });
      return;
    }

    if (needsRpaAgreement) {
      setSnack({ open: true, msg: "Please agree to the BlitzPay RPA Agreement first", sev: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await client.payouts.claim(token, {
        paymentMethod: "use_existing",
        paymentType: payout.forced_payment_rail,
        useExistingWallet: true,
        blitzpay: speedChoice === "fast",
      });

      if (response?.final_amount !== undefined) {
        setFinalAmount(response.final_amount);
      }

      setPaymentConfirmed(true);

      const railLabel = payout.forced_payment_rail === 'paypal' ? 'PayPal'
        : payout.forced_payment_rail === 'stripe' ? 'Stripe'
        : 'Bank ACH';

      setPayout(prev => ({
        ...prev,
        status: response?.payout_status || "approved",
        accepted_date: new Date().toISOString(),
        blitzpay: speedChoice === "fast",
        withdrawal_id: response?.withdrawal_id || prev?.withdrawal_id,
        withdrawal_processing_mode: response?.withdrawal_processing_mode || prev?.withdrawal_processing_mode,
        queued_for_manual_processing: response?.queued_for_manual_processing ?? prev?.queued_for_manual_processing,
      }));

      setSnack({
        open: true,
        msg: response?.message || `Payout claimed successfully! Payment will be sent to your ${railLabel} account.`,
        sev: "success",
      });

      setTimeout(() => { setShowNavigationDialog(true); }, 2000);

    } catch (err) {
      console.error("Existing wallet claim error:", err);
      let errorMessage = "Something went wrong. Please try again.";
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      setSnack({ open: true, msg: errorMessage, sev: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  /* ───────── Navigation handlers ───────── */
  const handleNavigateToAccount = () => {
    setShowNavigationDialog(false);
    if (payout?.has_creator_user) {
      navigate("/creatorconnect/login");
    } else if (payout?.creator_id) {
      navigate(`/creatorconnect/${payout.creator_id}`);
    } else {
      navigate("/creatorconnect/signup");
    }
  };

  const handleSkipAccount = () => {
    setShowNavigationDialog(false);
  };

  /* ───────── Get status display info ───────── */
  const getStatusInfo = () => {
    if (isQueuedForManualProcessing && payout?.status === "approved") {
      return {
        color: "warning",
        icon: <WarningIcon />,
        title: "Queued for Manual Processing",
        message: "Your payout has been queued for manual admin processing. An admin will complete the payout instead of BlitzPay auto-processing it.",
      };
    }

    switch (payout?.status) {
      case "approved":
        return {
          color: "success",
          icon: <CheckIcon />,
          title: "Payment Confirmed!",
          message: "Your payment method has been confirmed and the payout is being processed.",
        };
      case "confirmed":
        return {
          color: "success",
          icon: <CheckIcon />,
          title: "Payment Confirmed!",
          message: "Your payment has been confirmed and is being processed.",
        };
      case "paid":
        return {
          color: "success",
          icon: <CheckIcon />,
          title: "Payment Completed!",
          message: "Your payment has been successfully sent to your account.",
        };
      case "funded":
        return {
          color: "success",
          icon: <CheckIcon />,
          title: "Payment Funded!",
          message: "Your instant payment has been funded and is being sent to your account.",
        };
      case "cancelled":
        return {
          color: "error",
          icon: <BlockIcon />,
          title: "Payment Cancelled",
          message: "This payment has been cancelled and is no longer available.",
        };
      default:
        return {
          color: "warning",
          icon: <WarningIcon />,
          title: "Unknown Status",
          message: "The payout status is unclear. Please contact support.",
        };
    }
  };

  // Use the payout's actual interest_rate for fee deduction; fall back to 5% for NET30 instant opt-in (interest_rate=0)
  const blitzPayFeeRate = (payout?.interest_rate || 5) / 100;
  const calculatedAmount = isClientPayingFee
    ? payout?.amount
    : speedChoice === "fast"
      ? payout?.amount * (1 - blitzPayFeeRate)
      : payout?.amount;

  const creatorDisplayName = payout?.creator_display_name || payout?.creator_id || "Creator";

  const canProceedWithPayPal = (paypalOAuthConnected || (paypalEmail?.trim() && !emailError)) && !needsRpaAgreement;

  /* ───────── Navigation Dialog ───────── */
  const NavigationDialog = () => (
    <Dialog open={showNavigationDialog} onClose={handleSkipAccount} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="success" />
            Payment Confirmed!
          </Typography>
          <Button onClick={handleSkipAccount} size="small" sx={{ minWidth: 'auto', p: 1 }}>
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1">
            Your {money(finalAmount || calculatedAmount)} payment is being processed!
          </Typography>
          
          <Alert severity="success">
            <Typography variant="body2">
              <strong>PayPal Email Saved:</strong> Your PayPal email has been confirmed and saved. 
              Future payments will be even faster!
            </Typography>
          </Alert>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Optional:</strong> Create a Blitz account to track your earnings, manage payment methods, 
              and access additional creator features. You can skip this step if you prefer.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary">
            What would you like to do next?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button onClick={handleSkipAccount} variant="outlined">
          Skip for Now
        </Button>
        <Button 
          onClick={handleNavigateToAccount} 
          variant="contained"
          startIcon={<LaunchIcon />}
          sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
        >
          {payout?.has_creator_user ? "Go to Account" : "Create Account"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  /* ───────── BlitzPay RPA Agreement Section ───────── */
  const RpaAgreementSection = () => {
    if (rpaStatus.loading) {
      return (
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Checking agreement status...</Typography>
          </Box>
        </Card>
      );
    }

    if (rpaStatus.hasSigned && rpaCheckboxChecked) {
      return (
        <Card variant="outlined" sx={{ p: 3, mb: 3, bgcolor: "success.50" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckIcon color="success" />
            <Box>
              <Typography variant="h6" color="success.main">
                Agreement Confirmed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You've agreed to the BlitzPay RPA Agreement
              </Typography>
            </Box>
          </Box>
        </Card>
      );
    }

    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3, border: 2, borderColor: "primary.main" }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DocumentIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" color="primary">
                BlitzPay Repurchase Agreement Required
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please review and agree to receive instant payments
              </Typography>
            </Box>
            <Button
              onClick={() => setShowRpaDetails(!showRpaDetails)}
              endIcon={showRpaDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
            >
              {showRpaDetails ? "Hide" : "View"} Agreement
            </Button>
          </Box>

          <Collapse in={showRpaDetails}>
            <Paper variant="outlined" sx={{ p: 3, maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50' }}>
              {rpaTemplate ? (
                <Box
                  dangerouslySetInnerHTML={{ __html: rpaTemplate.contract_body }}
                  sx={{
                    '& h1, & h2, & h3': { mt: 2, mb: 1 },
                    '& p': { mb: 1 },
                    '& ul, & ol': { pl: 3, mb: 2 },
                  }}
                />
              ) : (
                <Typography color="text.secondary">Loading agreement...</Typography>
              )}
            </Paper>
          </Collapse>

          <Divider />

          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setRpaCheckboxChecked(!rpaCheckboxChecked)}
          >
            <Checkbox
              checked={rpaCheckboxChecked}
              onChange={(e) => setRpaCheckboxChecked(e.target.checked)}
              color="primary"
            />
            <Typography variant="body2">
              I have read and agree to the BlitzPay Repurchase Agreement
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleRpaAgreement}
            disabled={rpaProcessing || !paypalEmail || !!emailError || !rpaCheckboxChecked}
            fullWidth
            sx={{ py: 1.5 }}
          >
            {rpaProcessing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                Processing Agreement...
              </>
            ) : (
              "I Agree - Continue to Payment"
            )}
          </Button>

          {(!paypalEmail || emailError) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Please enter a valid PayPal email address first
              </Typography>
            </Alert>
          )}

          {!rpaCheckboxChecked && paypalEmail && !emailError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Please check the agreement checkbox above to proceed
              </Typography>
            </Alert>
          )}
        </Stack>
      </Card>
    );
  };

  if (loading) {
    return (
      <ThemeProvider theme={lightTheme || fallbackTheme}>
        <CssBaseline />
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Card sx={{ p: 4, textAlign: "center", minWidth: 300 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Loading your payment...</Typography>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  if (!payout) {
    return (
      <ThemeProvider theme={lightTheme || fallbackTheme}>
        <CssBaseline />
        <Box
          sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Card sx={{ p: 4, textAlign: "center", maxWidth: 400 }}>
            <Typography variant="h5" color="error" gutterBottom>
              Payment Not Found
            </Typography>
            <Typography color="text.secondary">
              This payment link may have expired or been already claimed.
            </Typography>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <ThemeProvider theme={lightTheme || fallbackTheme}>
      <CssBaseline />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <HomeHeader />
        <Toolbar />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", py: 4 }}>
          <Container maxWidth="md">
            {!canClaimPayout ? (
              <Card elevation={3}>
                <CardContent sx={{ p: 4, textAlign: "center" }}>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                    {statusInfo.icon}
                  </Box>
                  <Typography variant="h3" fontWeight="bold" gutterBottom color={`${statusInfo.color}.main`}>
                    {statusInfo.title}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Hello {creatorDisplayName}, your {money(payout.amount)} payment status:
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
                    {statusInfo.message}
                  </Typography>
                  {isAlreadyClaimed && payout.payout_method && (
                    <Card variant="outlined" sx={{ mt: 3, bgcolor: "grey.50" }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Payment Details
                        </Typography>
                        <Stack spacing={1}>
                          <Typography>
                            <strong>Method:</strong> {payout.payout_method}
                          </Typography>
                          <Typography>
                            <strong>Amount:</strong> {money(payout.amount)}
                          </Typography>
                          {payout.accepted_date && (
                            <Typography>
                              <strong>Confirmed:</strong> {new Date(payout.accepted_date).toLocaleDateString()}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card elevation={3}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      Claim Your Payment
                    </Typography>
                    <Typography variant="h5" sx={{ opacity: 0.9, mb: 2 }}>
                      Hello {creatorDisplayName}, you have{" "}
                      <strong>{money(payout?.amount)}</strong> waiting for you.
                    </Typography>
                  </Box>

                  {isClientPayingFee && (
                    <Alert severity="success" sx={{ mb: 4 }}>
                      <Typography variant="body2">
                        <strong>Great news!</strong> The client has paid all processing fees. 
                        You'll receive the full amount with instant processing at no cost to you.
                      </Typography>
                    </Alert>
                  )}

                  {!isClientPayingFee && (
                    <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 4 }}>
                      <Typography variant="body2">
                        <strong>Payment Options:</strong> PayPal charges their own fees that we cannot control. 
                        For lower fees, create a Blitz account and link your bank account for direct bank transfers.
                      </Typography>
                    </Alert>
                  )}

                  {/* Locked rail info banner */}
                  {isRailForced && (
                    <Alert severity={hasMatchingWallet ? "success" : "info"} icon={<InfoIcon />} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Payment method: {payout.forced_payment_rail === 'paypal' ? 'PayPal' : payout.forced_payment_rail === 'stripe' ? 'Stripe' : payout.forced_payment_rail === 'bank_ach' ? 'Bank ACH' : payout.forced_payment_rail}
                      </Typography>
                      <Typography variant="body2">
                        {hasMatchingWallet
                          ? `This payout will be sent to your existing account: ${matchingWalletDisplay}`
                          : 'The sender has locked this payout to a specific payment method. Please provide your details below.'
                        }
                      </Typography>
                    </Alert>
                  )}

                  <Grid container spacing={4}>
                    {!isRailForced && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Choose Payment Method
                      </Typography>
                      
                      <Stack spacing={2}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            p: 3, 
                            cursor: 'pointer',
                            border: paymentChoice === 'paypal' ? 2 : 1,
                            borderColor: paymentChoice === 'paypal' ? 'primary.main' : 'grey.300',
                            backgroundColor: paymentChoice === 'paypal' ? 'primary.50' : 'white',
                          }}
                          onClick={() => setPaymentChoice('paypal')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Radio
                              checked={paymentChoice === 'paypal'}
                              onChange={() => setPaymentChoice('paypal')}
                              value="paypal"
                            />
                            <EmailIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" color="primary">
                                PayPal (Quick Setup)
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {isClientPayingFee 
                                  ? "Get paid instantly with just your email - no fees!"
                                  : "Fast setup with just your email"
                                }
                              </Typography>
                              {!isClientPayingFee && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                  ⚠️ PayPal charges separate fees we cannot control
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Card>

                        <Card
                          variant="outlined"
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            border: paymentChoice === 'bank_ach' ? 2 : 1,
                            borderColor: paymentChoice === 'bank_ach' ? 'primary.main' : 'grey.300',
                            backgroundColor: paymentChoice === 'bank_ach' ? 'primary.50' : 'white',
                          }}
                          onClick={() => setPaymentChoice('bank_ach')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Radio
                              checked={paymentChoice === 'bank_ach'}
                              onChange={() => setPaymentChoice('bank_ach')}
                              value="bank_ach"
                            />
                            <OtherIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" color="primary">
                                Bank Account (ACH)
                              </Typography>
                              {payout?.has_international_bank_wallet ? (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    Sent via Wise — converts to {payout.international_bank_currency} · 1–2 business days
                                  </Typography>
                                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                    ⚠ Subject to currency conversion fees
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    Direct deposit to your bank - 1-3 business days
                                  </Typography>
                                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                    ✓ Lowest fees - No third-party charges
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Card>

                        <Card
                          variant="outlined"
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            border: paymentChoice === 'stripe' ? 2 : 1,
                            borderColor: paymentChoice === 'stripe' ? 'primary.main' : 'grey.300',
                            backgroundColor: paymentChoice === 'stripe' ? 'primary.50' : 'white',
                          }}
                          onClick={() => setPaymentChoice('stripe')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Radio
                              checked={paymentChoice === 'stripe'}
                              onChange={() => setPaymentChoice('stripe')}
                              value="stripe"
                            />
                            <OtherIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" color="primary">
                                Stripe Connect
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {stripeConnected ? "Account connected - Ready to receive" : "Connect once, get paid fast"}
                              </Typography>
                              {stripeConnected && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                  ✓ Stripe account connected
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Card>

                        <Card
                          variant="outlined"
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            border: paymentChoice === 'other' ? 2 : 1,
                            borderColor: paymentChoice === 'other' ? 'primary.main' : 'grey.300',
                            backgroundColor: paymentChoice === 'other' ? 'primary.50' : 'white',
                          }}
                          onClick={() => setPaymentChoice('other')}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Radio
                              checked={paymentChoice === 'other'}
                              onChange={() => setPaymentChoice('other')}
                              value="other"
                            />
                            <OtherIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" color="primary">
                                Other Methods
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Wire, Venmo, Crypto & more (requires account)
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>
                    )}

                    <Grid item xs={12} md={isRailForced ? 12 : 6}>
                      <Stack spacing={3}>
                        {/* ── Seamless claim: existing wallet found ── */}
                        {hasMatchingWallet && (
                          <>
                            <Card variant="outlined" sx={{ p: 3, borderColor: 'success.main', borderWidth: 2, backgroundColor: 'success.50' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <CheckIcon color="success" />
                                <Typography variant="h6">Payment Destination</Typography>
                              </Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {matchingWalletDisplay}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                This payout will be sent to your existing {payout.forced_payment_rail === 'paypal' ? 'PayPal' : payout.forced_payment_rail === 'stripe' ? 'Stripe' : 'Bank'} account.
                              </Typography>
                            </Card>

                            <RpaAgreementSection />

                            {!isClientPayingFee && (
                              <Box>
                                <FormControl>
                                  <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <SpeedIcon color="primary" />
                                    <Typography variant="h6">Processing Speed</Typography>
                                  </FormLabel>
                                  <RadioGroup
                                    value={speedChoice}
                                    onChange={(e) => setSpeedChoice(e.target.value)}
                                  >
                                    <FormControlLabel
                                      value="wait"
                                      control={<Radio />}
                                      label={
                                        <Box>
                                          <Typography variant="body1" fontWeight="bold">
                                            Standard - {money(payout?.amount)} (Free)
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            30-90 business days
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ p: 1, borderRadius: 1, bgcolor: speedChoice === "wait" ? "action.selected" : "transparent" }}
                                    />
                                    <FormControlLabel
                                      value="fast"
                                      control={<Radio />}
                                      label={
                                        <Box>
                                          <Typography variant="body1" fontWeight="bold">
                                            Instant BlitzPay - {money(payout?.amount * (1 - blitzPayFeeRate))} ({payout?.interest_rate || 5}% fee)
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Receive within 24-72 hours
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ p: 1, borderRadius: 1, bgcolor: speedChoice === "fast" ? "action.selected" : "transparent" }}
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Box>
                            )}

                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              onClick={handleExistingWalletClaim}
                              disabled={submitting || needsRpaAgreement}
                              sx={{ py: 2, fontSize: '1.1rem' }}
                            >
                              {submitting ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : (
                                `Claim $${Number(payout?.amount || 0).toFixed(2)}`
                              )}
                            </Button>
                          </>
                        )}

                        {/* ── Standard claim: enter payment details ── */}
                        {!hasMatchingWallet && paymentChoice === 'paypal' && (
                          <>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                PayPal
                              </Typography>

                              {!isClientPayingFee && (
                                <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 2 }}>
                                  <Typography variant="body2">
                                    <strong>Note:</strong> PayPal charges their own processing fees that Blitz cannot control.
                                    For the lowest fees, we recommend setting up a bank transfer instead.
                                  </Typography>
                                </Alert>
                              )}

                              {/* PayPal OAuth connected badge */}
                              {paypalOAuthConnected && (
                                <Box sx={{ mb: 2 }}>
                                  <PayPalVerifiedBadge email={paypalOAuthEmail} />
                                </Box>
                              )}

                              {/* PayPal OAuth connect button (reusable component) */}
                              {!paypalOAuthConnected && (
                                <Box sx={{ mb: 2 }}>
                                  <PayPalConnectButton
                                    claimToken={token}
                                    onError={handlePaypalOAuthError}
                                    fullWidth
                                    size="large"
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                    — or enter your PayPal email manually —
                                  </Typography>
                                </Box>
                              )}

                              <TextField
                                fullWidth
                                required={!paypalOAuthConnected}
                                label="PayPal Email Address"
                                placeholder="your.email@paypal.com"
                                value={paypalEmail}
                                onChange={handlePaypalEmailChange}
                                error={!!emailError}
                                helperText={
                                  emailError || (paypalOAuthConnected ? "Verified via PayPal login" : "Enter the email address linked to your PayPal account")
                                }
                                disabled={paypalOAuthConnected}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <EmailIcon />
                                    </InputAdornment>
                                  ),
                                }}
                              />

                              {emailWarning && emailWarning.includes("Did you mean") && (
                                <Alert
                                  severity="warning"
                                  sx={{ mt: 1 }}
                                  action={
                                    <Button color="inherit" size="small" onClick={acceptSuggestion}>
                                      Fix It
                                    </Button>
                                  }
                                >
                                  <Typography variant="body2">{emailWarning}</Typography>
                                </Alert>
                              )}
                            </Box>

                            <RpaAgreementSection />

                            {!isClientPayingFee && (
                              <Box>
                                <FormControl>
                                  <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <SpeedIcon color="primary" />
                                    <Typography variant="h6">Processing Speed</Typography>
                                  </FormLabel>
                                  <RadioGroup
                                    value={speedChoice}
                                    onChange={(e) => setSpeedChoice(e.target.value)}
                                  >
                                    <FormControlLabel
                                      value="wait"
                                      control={<Radio />}
                                      label={
                                        <Box>
                                          <Typography variant="body1" fontWeight="bold">
                                            Standard - {money(payout?.amount)} (Free)
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            30-90 business days
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: speedChoice === "wait" ? "action.selected" : "transparent"
                                      }}
                                    />
                                    <FormControlLabel
                                      value="fast"
                                      control={<Radio />}
                                      label={
                                        <Box>
                                          <Typography variant="body1" fontWeight="bold">
                                            Instant - {money(payout?.amount * (1 - blitzPayFeeRate))} ({payout?.interest_rate || 5}% fee)
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Processed within 24-72 hours
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: speedChoice === "fast" ? "action.selected" : "transparent"
                                      }}
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Box>
                            )}

                            {isClientPayingFee && (
                              <Box>
                                <FormControl>
                                  <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <InstantIcon color="success" />
                                    <Typography variant="h6" color="success.main">
                                      Instant Processing (Fee Paid)
                                    </Typography>
                                  </FormLabel>
                                  <Card variant="outlined" sx={{ p: 2, bgcolor: "success.50" }}>
                                    <Typography variant="body1" fontWeight="bold" color="success.main">
                                      Full Amount - {money(payout?.amount)} (No deductions)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Processed within 24-72 hours - Client has covered all fees
                                    </Typography>
                                  </Card>
                                </FormControl>
                              </Box>
                            )}
                          </>
                        )}

                        {!hasMatchingWallet && paymentChoice === 'bank_ach' && (
                          <>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Bank Account
                              </Typography>

                              <Alert severity="success" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                  <strong>Lowest fees!</strong> Direct ACH transfers have no additional processing fees.
                                </Typography>
                              </Alert>

                              {/* Stripe FC connect */}
                              {stripeFCConnected ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.light', borderRadius: 2, border: '1px solid', borderColor: 'success.main', mb: 2 }}>
                                  <CheckIcon color="success" />
                                  <Box>
                                    <Typography variant="body1" fontWeight={600} color="success.dark">Bank Account Connected</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {stripeFCBankInfo?.bank_name} ****{stripeFCBankInfo?.last4}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ mb: 2 }}>
                                  <StripePlatformFCButton
                                    claimToken={token}
                                    fullWidth
                                    size="large"
                                    label="Link Bank Account"
                                    onSuccess={(bankInfo) => {
                                      setStripeFCConnected(true);
                                      setStripeFCBankInfo(bankInfo);
                                      setSnack({ open: true, msg: "Bank account linked!", sev: "success" });
                                    }}
                                    onError={() => setSnack({ open: true, msg: "Failed to link bank account. Enter details manually.", sev: "warning" })}
                                    sx={{ mb: 1 }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                    — or enter your bank details manually —
                                  </Typography>
                                </Box>
                              )}

                              <Stack spacing={2} sx={{ display: stripeFCConnected ? 'none' : undefined }}>
                                <TextField
                                  fullWidth
                                  required
                                  label="Account Holder Name"
                                  placeholder="John Smith"
                                  value={bankDetails.account_holder_name}
                                  onChange={handleBankDetailChange('account_holder_name')}
                                  error={!!bankErrors.account_holder_name}
                                  helperText={bankErrors.account_holder_name || "Name as it appears on your bank account"}
                                />

                                <TextField
                                  fullWidth
                                  required
                                  label="Routing Number"
                                  placeholder="123456789"
                                  value={bankDetails.routing_number}
                                  onChange={handleBankDetailChange('routing_number')}
                                  error={!!bankErrors.routing_number}
                                  helperText={bankErrors.routing_number || "9-digit routing number"}
                                  inputProps={{ maxLength: 9 }}
                                />

                                <TextField
                                  fullWidth
                                  required
                                  label="Account Number"
                                  placeholder="••••••••1234"
                                  value={bankDetails.account_number}
                                  onChange={handleBankDetailChange('account_number')}
                                  error={!!bankErrors.account_number}
                                  helperText={bankErrors.account_number || "Your bank account number"}
                                  inputProps={{ maxLength: 17 }}
                                />

                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={handleCreateManualBank}
                                  disabled={manualBankCreating}
                                  sx={{ mt: 1 }}
                                >
                                  {manualBankCreating ? (
                                    <><CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />Linking...</>
                                  ) : (
                                    'Create Bank Account'
                                  )}
                                </Button>
                              </Stack>
                            </Box>

                            <RpaAgreementSection />

                            {!isClientPayingFee && (
                              <Box>
                                <FormControl>
                                  <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <SpeedIcon color="primary" />
                                    <Typography variant="h6">Processing Speed</Typography>
                                  </FormLabel>
                                  <RadioGroup
                                    value={speedChoice}
                                    onChange={(e) => setSpeedChoice(e.target.value)}
                                  >
                                    <FormControlLabel
                                      value="wait"
                                      control={<Radio />}
                                      label={
                                        <Box>
                                          <Typography variant="body1" fontWeight="bold">
                                            Standard - {money(payout?.amount)} (Free)
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            30-90 business days
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ p: 1, borderRadius: 1, bgcolor: speedChoice === "wait" ? "action.selected" : "transparent" }}
                                    />
                                    <FormControlLabel
                                      value="fast"
                                      control={<Radio />}
                                      label={
                                        <Box>
                                          <Typography variant="body1" fontWeight="bold">
                                            Instant - {money(payout?.amount * (1 - blitzPayFeeRate))} ({payout?.interest_rate || 5}% fee)
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Processed within 1-3 business days
                                          </Typography>
                                        </Box>
                                      }
                                      sx={{ p: 1, borderRadius: 1, bgcolor: speedChoice === "fast" ? "action.selected" : "transparent" }}
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Box>
                            )}

                            {isClientPayingFee && (
                              <Box>
                                <Card variant="outlined" sx={{ p: 2, bgcolor: "success.50" }}>
                                  <Typography variant="body1" fontWeight="bold" color="success.main">
                                    Full Amount - {money(payout?.amount)} (No deductions)
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Processed within 1-3 business days - Client has covered all fees
                                  </Typography>
                                </Card>
                              </Box>
                            )}
                          </>
                        )}

                        {!hasMatchingWallet && paymentChoice === 'stripe' && (
                          <>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Stripe Connect
                              </Typography>

                              {!stripeConnected ? (
                                <>
                                  <Alert severity="info" sx={{ mb: 3 }}>
                                    <Typography variant="body2">
                                      Connect your Stripe account to receive payments directly. This is a one-time setup.
                                    </Typography>
                                  </Alert>

                                  <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    onClick={handleStripeConnect}
                                    disabled={stripeConnecting}
                                    sx={{
                                      background: "linear-gradient(135deg, #635bff 0%, #7a73ff 100%)",
                                      py: 2,
                                    }}
                                  >
                                    {stripeConnecting ? (
                                      <>
                                        <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                                        Connecting...
                                      </>
                                    ) : (
                                      "Connect with Stripe"
                                    )}
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Alert severity="success" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                      <strong>Stripe account connected!</strong> You're ready to receive payments.
                                    </Typography>
                                  </Alert>

                                  <RpaAgreementSection />

                                  {!isClientPayingFee && (
                                    <Box>
                                      <FormControl>
                                        <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                          <SpeedIcon color="primary" />
                                          <Typography variant="h6">Processing Speed</Typography>
                                        </FormLabel>
                                        <RadioGroup
                                          value={speedChoice}
                                          onChange={(e) => setSpeedChoice(e.target.value)}
                                        >
                                          <FormControlLabel
                                            value="wait"
                                            control={<Radio />}
                                            label={
                                              <Box>
                                                <Typography variant="body1" fontWeight="bold">
                                                  Standard - {money(payout?.amount)} (Free)
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  30-90 business days
                                                </Typography>
                                              </Box>
                                            }
                                            sx={{ p: 1, borderRadius: 1, bgcolor: speedChoice === "wait" ? "action.selected" : "transparent" }}
                                          />
                                          <FormControlLabel
                                            value="fast"
                                            control={<Radio />}
                                            label={
                                              <Box>
                                                <Typography variant="body1" fontWeight="bold">
                                                  Instant - {money(payout?.amount * (1 - blitzPayFeeRate))} ({payout?.interest_rate || 5}% fee)
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  Processed within 1-2 business days
                                                </Typography>
                                              </Box>
                                            }
                                            sx={{ p: 1, borderRadius: 1, bgcolor: speedChoice === "fast" ? "action.selected" : "transparent" }}
                                          />
                                        </RadioGroup>
                                      </FormControl>
                                    </Box>
                                  )}

                                  {isClientPayingFee && (
                                    <Box>
                                      <Card variant="outlined" sx={{ p: 2, bgcolor: "success.50" }}>
                                        <Typography variant="body1" fontWeight="bold" color="success.main">
                                          Full Amount - {money(payout?.amount)} (No deductions)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Processed within 1-2 business days - Client has covered all fees
                                        </Typography>
                                      </Card>
                                    </Box>
                                  )}
                                </>
                              )}
                            </Box>
                          </>
                        )}

                        {paymentChoice === 'other' && (
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              Additional Payment Methods
                            </Typography>

                            <Alert severity="info" sx={{ mb: 3 }}>
                              <Typography variant="body2">
                                Create a Blitz account to access additional payment methods including wire transfers,
                                Venmo, cryptocurrency, and international options.
                              </Typography>
                            </Alert>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Available with a Blitz account:
                            </Typography>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                • Wire transfers (US & International)
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • Venmo
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • Cryptocurrency wallets
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                • And more...
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>

                  {!hasMatchingWallet && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4, pt: 2, borderTop: 1, borderColor: "divider" }}>
                    {paymentChoice === 'paypal' && (
                      <Button
                        variant="contained"
                        onClick={handlePayPalSubmit}
                        disabled={!canProceedWithPayPal || submitting}
                        size="large"
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          minWidth: 250,
                          py: 1.5,
                        }}
                      >
                        {submitting ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                            Processing...
                          </>
                        ) : (
                          `Claim ${money(calculatedAmount)} with PayPal`
                        )}
                      </Button>
                    )}

                    {paymentChoice === 'bank_ach' && payout?.has_international_bank_wallet && (
                      <Box
                        sx={{
                          p: 2,
                          mb: 1,
                          borderRadius: 1,
                          backgroundColor: 'warning.50',
                          border: '1px solid',
                          borderColor: 'warning.main',
                        }}
                      >
                        <Typography variant="body2" color="warning.dark" fontWeight="bold">
                          Currency conversion fee notice
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Your payment will be converted from USD to <strong>{payout.international_bank_currency}</strong> via Wise.
                          You will be charged a conversion fee (1.5% transfer fee + 0.5% FX markup over the mid-market rate).
                          The amount you receive may vary based on exchange rates at the time of transfer.
                        </Typography>
                      </Box>
                    )}

                    {paymentChoice === 'bank_ach' && (
                      <Button
                        variant="contained"
                        onClick={handleBankAchSubmit}
                        disabled={submitting || needsRpaAgreement}
                        size="large"
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          minWidth: 250,
                          py: 1.5,
                        }}
                      >
                        {submitting ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                            Processing...
                          </>
                        ) : payout?.has_international_bank_wallet ? (
                          `Claim ${money(calculatedAmount)} via Wise (${payout.international_bank_currency})`
                        ) : (
                          `Claim ${money(calculatedAmount)} via Bank Transfer`
                        )}
                      </Button>
                    )}

                    {paymentChoice === 'stripe' && stripeConnected && (
                      <Button
                        variant="contained"
                        onClick={handleStripeSubmit}
                        disabled={submitting || needsRpaAgreement}
                        size="large"
                        sx={{
                          background: "linear-gradient(135deg, #635bff 0%, #7a73ff 100%)",
                          minWidth: 250,
                          py: 1.5,
                        }}
                      >
                        {submitting ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                            Processing...
                          </>
                        ) : (
                          `Claim ${money(calculatedAmount)} via Stripe`
                        )}
                      </Button>
                    )}

                    {paymentChoice === 'other' && (
                      <Button
                        variant="contained"
                        onClick={handleOtherPaymentMethod}
                        size="large"
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          minWidth: 250,
                          py: 1.5,
                        }}
                      >
                        Create Account & Claim {money(payout?.amount)}
                      </Button>
                    )}
                  </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Container>
        </Box>
        <HomeFooter />
        
        <NavigationDialog />
        
        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          <Alert
            onClose={() => setSnack({ ...snack, open: false })}
            severity={snack.sev}
            sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>

        <StripeOnboardingRequiredModal
          open={onboardingModalOpen}
          onClose={() => setOnboardingModalOpen(false)}
          missingFields={onboardingBlock?.missingFields || achMissingFields}
          onboardingUrl={onboardingBlock?.onboardingUrl || achOnboardingUrl}
          context="claim"
        />
      </Box>
    </ThemeProvider>
  );
};

export default PayoutClaimPage;
